import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES, MITAKA_XZ, type KanjinSite } from '../kanjinData'
import { ICHIJO_MEMBERS, siteOfMember } from './Proto8'

/**
 * ⑪ 日本中から、一畳が組み上がる（⑤大圏弧＋ニアミス × ⑧フレーム実体化の合体）。
 * 三鷹の上空に巨大な一畳敷ワイヤーフレームが浮かび、12の寄進地から木片が
 * 日本列島を越えて飛来（2片は視点すれすれを通過）、対応する部材が1本ずつ実体化する。
 */
export class Proto11 extends ProtoBase {
  readonly title = '11. 日本中から、一畳が組み上がる'
  readonly desc = '⑤+⑧合体: 全国から木片が弧を描いて飛来し、関東沖上空の一畳敷フレームが部材ごとに実体化（地図と重ならない配置・自由視点）'

  /** フレームの拡大率と設置位置。日本列島と重ならないよう、関東沖の上空に離して浮かべる */
  private static readonly SCALE = 9
  private static readonly ORIGIN = new THREE.Vector3(MITAKA_XZ.x + 26, 30, MITAKA_XZ.z + 30)

  private members: { lines: THREE.LineSegments[]; revealed: boolean; targets: THREE.Vector3[] }[] = []
  private frame = new THREE.Group()
  private next = 0
  private launchTimer = 0
  private doneT = 0
  private nearIdx: number[] = []
  private whooshed = new Set<number>()
  private glow!: THREE.Sprite
  private tmp = new THREE.Vector3()

  // 視点は初期配置のみ（以降は自由視点: PC=OrbitControls / VR=HMD）
  private poseInit = {
    pos: new THREE.Vector3(-14, 92, 132),
    tgt: new THREE.Vector3(6, 12, 12),
  }
  private poseInitXr = { pos: new THREE.Vector3(0, 0, 70), tgt: new THREE.Vector3(10, 8, 20) }

  protected start(): void {
    this.ctx.setWorld('map')
    this.ctx.setCaption('三鷹の空に、まだ形のない一畳が待っている——')

    // 巨大一畳敷フレーム（三鷹上空・部材は透明で待機）
    this.frame.position.copy(Proto11.ORIGIN)
    this.frame.scale.setScalar(Proto11.SCALE)
    this.frame.rotation.y = Math.PI / 6
    this.group.add(this.frame)

    for (const m of ICHIJO_MEMBERS) {
      const set: THREE.LineSegments[] = []
      const targets: THREE.Vector3[] = []
      for (const b of m.boxes) {
        const geo = new THREE.EdgesGeometry(new THREE.BoxGeometry(...b.size))
        const mat = new THREE.LineBasicMaterial({ color: m.color ?? 0xffd77a, transparent: true, opacity: 0.06 })
        const ls = new THREE.LineSegments(geo, mat)
        ls.position.set(...b.pos)
        this.frame.add(ls)
        set.push(ls)
        targets.push(this.frame.localToWorld(new THREE.Vector3(...b.pos)))
      }
      this.members.push({ lines: set, revealed: false, targets })
    }

    // 完成グロー
    this.glow = Proto11.makeGlow()
    this.glow.position.copy(Proto11.ORIGIN)
    this.glow.visible = false
    this.group.add(this.glow)

    this.launchTimer = 1.5
    // 初期視点のみ設定（以降は自由視点。演出がカメラを奪わない）
    const p = this.ctx.view.xrActive ? this.poseInitXr : this.poseInit
    this.ctx.view.setPose(p.pos.clone(), p.tgt.clone())
  }

  update(dt: number): void {
    this.elapsed += dt
    this.launchTimer -= dt

    // 発射: 各寄進地 → 対応部材へ（2片はニアミス経由）
    if (this.next < this.members.length && this.launchTimer <= 0) {
      const i = this.next++
      this.launchTimer = 1.7
      const site: KanjinSite = siteOfMember(i)
      const from = new THREE.Vector3()
      this.ctx.markers.topOf(site.no, from)
      const to = this.members[i].targets[0].clone()

      let via: THREE.Vector3 | undefined
      if (i === 3 || i === 7) {
        // ニアミス: 視点のすぐ横を経由
        const eye = this.ctx.view.eyePosition(new THREE.Vector3())
        const fwd = new THREE.Vector3()
        this.ctx.camera.getWorldDirection(fwd)
        via = eye.clone().addScaledVector(fwd, 12).add(new THREE.Vector3(i === 3 ? 5 : -5, -2, 0))
      }
      const idx = this.ctx.chips.launch({
        from, to, via,
        apex: via ? undefined : 18 + Math.random() * 14,
        dur: via ? 4.0 : 3.2,
        site,
        scale: via ? 1.5 : 1,
      })
      if (via && idx >= 0) this.nearIdx.push(idx)

      this.ctx.markers.highlight(site.no, 1)
      this.ctx.chips.onArrive = (s) => {
        // 直近到着分を実体化（発射順に到着する前提の簡易対応）
        const firstHidden = this.members.findIndex((m) => !m.revealed)
        if (firstHidden < 0) return
        const m = this.members[firstHidden]
        m.revealed = true
        const memberName = ICHIJO_MEMBERS[firstHidden].name
        this.ctx.sfx.play(firstHidden === this.members.length - 1 ? 'drum' : 'chime', m.targets[0])
        this.ctx.setCaption(`${memberName}\n（${s?.building ?? ''}より飛来）`)
      }
    }

    // ニアミス風切り
    for (const idx of this.nearIdx) {
      if (this.whooshed.has(idx)) continue
      if (this.ctx.chips.positionOf(idx, this.tmp)) {
        if (this.tmp.distanceTo(this.ctx.view.eyePosition(new THREE.Vector3())) < 13) {
          this.whooshed.add(idx)
          this.ctx.sfx.play('whoosh', this.tmp)
        }
      }
    }

    // 実体化フェードイン
    for (const m of this.members) {
      if (!m.revealed) continue
      for (const ls of m.lines) {
        const mat = ls.material as THREE.LineBasicMaterial
        if (mat.opacity < 1) mat.opacity = Math.min(1, mat.opacity + dt * 2.2)
      }
    }

    // フレームはゆっくり回る（浮遊感）
    this.frame.rotation.y += dt * 0.05

    const allDone = this.members.every((m) => m.revealed)
    if (allDone && this.doneT === 0) {
      this.doneT = this.elapsed
      this.glow.visible = true
      this.ctx.sfx.play('bell', Proto11.ORIGIN)
      this.ctx.setCaption('一畳敷、成る——六十六人の勧進、八十九の木片、日本中の千年')
    }
    if (this.doneT > 0) {
      const t = this.elapsed - this.doneT
      const pulse = 0.75 + 0.25 * Math.sin(t * 3)
      this.members.forEach((m) => m.lines.forEach((ls) => ((ls.material as THREE.LineBasicMaterial).opacity = pulse)))
      this.glow.scale.setScalar(26 * (1 + 0.08 * Math.sin(t * 5)))
      if (t > 9) {
        // ループ
        this.doneT = 0
        this.next = 0
        this.launchTimer = 1.5
        this.nearIdx = []
        this.whooshed.clear()
        this.glow.visible = false
        this.ctx.chips.clear()
        this.ctx.markers.clearHighlights()
        this.members.forEach((m) => {
          m.revealed = false
          m.lines.forEach((ls) => ((ls.material as THREE.LineBasicMaterial).opacity = 0.06))
        })
        this.ctx.setCaption('三鷹の空に、まだ形のない一畳が待っている——')
      }
    }
  }

  protected onDispose(): void {
    this.ctx.chips.onArrive = undefined
    this.ctx.chips.clear()
    this.ctx.markers.clearHighlights()
  }

  private static makeGlow(): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = c.height = 128
    const ctx = c.getContext('2d')!
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
    g.addColorStop(0, 'rgba(255,232,170,0.95)')
    g.addColorStop(0.5, 'rgba(255,195,100,0.4)')
    g.addColorStop(1, 'rgba(255,185,90,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 128, 128)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false,
    }))
  }
}
